<div class="app-dialog">
  <div class="app-dialog__heading">
    <button mat-icon-button type="button" (click)="onCancel()" class="start-project-dialog-close" aria-label="Close" [disabled]="loading">
      <mat-icon>close</mat-icon>
    </button>
    <div class="app-dialog__heading-copy">
      <span class="app-dialog__eyebrow">Project</span>
      <div class="app-dialog__title-row">
        <div class="app-dialog__title-copy">
          <h2 mat-dialog-title class="app-dialog__title">Start Project: {{ data.project.projectName }}</h2>
          <p>Start this project with its configured data source or upload a new file.</p>
        </div>
      </div>
    </div>
  </div>
  
  <mat-dialog-content>
    <div class="dialog-content">
      <!-- API Listener Instructions -->
      <div *ngIf="isApiListener" class="api-listener-section">
        <div class="info-banner info-box">
          <mat-icon svgIcon="info-circle"></mat-icon>
          <span>This project uses an <strong>API Listener</strong> data source. Post your data to the endpoint below to trigger processing.</span>
        </div>

        <!-- API Call Toggle -->
        <mat-button-toggle-group [(ngModel)]="selectedApiCall" class="api-call-toggle">
          <mat-button-toggle value="start">1. Start Cleaning</mat-button-toggle>
          <mat-button-toggle value="status">2. Get Status</mat-button-toggle>
          <mat-button-toggle value="results">3. Get Results</mat-button-toggle>
        </mat-button-toggle-group>

        <p *ngIf="selectedApiCall === 'start'" class="api-instructions">To clean data with this project, send a POST request to the endpoint below with your data in JSON format. The project will automatically process the data according to your configured goals and destinations.</p>
        <p *ngIf="selectedApiCall === 'status'" class="api-instructions">After making the start call, you will receive an action token. Use this token to poll the endpoint below every few seconds until the status is <strong>2 (Complete)</strong> or <strong>3 (Failed)</strong>.</p>
        <p *ngIf="selectedApiCall === 'results'" class="api-instructions">Once the status endpoint returns a status of <strong>2 (Complete)</strong>, call the endpoint below using the same action token to retrieve the cleaned data.</p>

        <div *ngIf="selectedApiCall === 'start'" class="api-config-section">
          <div class="config-item">
            <label>Endpoint</label>
            <div class="input-with-copy">
              <input type="text" [value]="endpoint" readonly class="endpoint-input">
              <button mat-icon-button (click)="copyToClipboard(endpoint, 'endpoint')" [title]="endpointJustCopied ? 'Copied!' : 'Copy endpoint'">
                <mat-icon [class.copied]="endpointJustCopied">{{ endpointJustCopied ? 'check' : 'content_copy' }}</mat-icon>
              </button>
            </div>
          </div>

          <div class="config-item">
            <label>Token</label>
            <div class="input-with-copy">
              <input type="text" [value]="token" readonly class="token-input" [class.placeholder-token]="!isTokenAvailable()">
              <button 
                *ngIf="isTokenAvailable()"
                mat-icon-button 
                (click)="copyToClipboard(token, 'token')" 
                [title]="tokenJustCopied ? 'Copied!' : 'Copy token'">
                <mat-icon [class.copied]="tokenJustCopied">{{ tokenJustCopied ? 'check' : 'content_copy' }}</mat-icon>
              </button>
              <button 
                *ngIf="!isTokenAvailable()"
                mat-stroked-button 
                class="regenerate-btn"
                (click)="regenerateToken()" 
                [disabled]="isRegeneratingToken">
                <mat-icon *ngIf="!isRegeneratingToken">refresh</mat-icon>
                <mat-spinner *ngIf="isRegeneratingToken" diameter="20"></mat-spinner>
                {{ isRegeneratingToken ? 'Regenerating...' : 'Regenerate Token' }}
              </button>
            </div>
            <div *ngIf="tokenRegenerationError" class="error-message">
              <mat-icon svgIcon="error"></mat-icon>
              {{ tokenRegenerationError }}
            </div>
          </div>

          <div class="config-item">
            <label>Request Method</label>
            <div class="method-badge">POST</div>
          </div>

          <div class="config-item">
            <label>Headers</label>
            <div class="headers-info">
              <div class="header-item">
                <span class="header-name">Content-Type:</span>
                <span class="header-value">application/json</span>
              </div>
              <div class="header-item">
                <span class="header-name">Authorization:</span>
                <span class="header-value">Bearer {{ token }}</span>
              </div>
            </div>
          </div>

          <div class="config-item">
            <label>Code Examples</label>
            <mat-tab-group class="code-samples-tabs">
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="curl"></mat-icon>
                    <span>cURL</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getCurlCommand()" [language]="'bash'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button 
                          class="copy-code-button"
                          (click)="copyToClipboard(getCurlCommand(), 'code')"
                          [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                      <mat-icon svgIcon="csharp"></mat-icon>
                      <span>C#</span>
                    </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getCSharpCode()" [language]="'csharp'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button 
                          class="copy-code-button"
                          (click)="copyToClipboard(getCSharpCode(), 'code')"
                          [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                      <mat-icon svgIcon="vbnet"></mat-icon>
                      <span>VB.NET</span>
                    </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getVBNetCode()" [language]="'vbnet'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button 
                          class="copy-code-button"
                          (click)="copyToClipboard(getVBNetCode(), 'code')"
                          [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="python"></mat-icon>
                    <span>Python</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getPythonCode()" [language]="'python'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button 
                          class="copy-code-button"
                          (click)="copyToClipboard(getPythonCode(), 'code')"
                          [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon>code</mat-icon>
                    <span>Java</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getJavaCode()" [language]="'java'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button 
                          class="copy-code-button"
                          (click)="copyToClipboard(getJavaCode(), 'code')"
                          [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
            </mat-tab-group>
          </div>
        </div>

        <!-- Status Call Section -->
        <div *ngIf="selectedApiCall === 'status'" class="api-config-section">
          <div class="config-item">
            <label>Endpoint</label>
            <div class="input-with-copy">
              <input type="text" [value]="statusEndpoint" readonly class="endpoint-input">
              <button mat-icon-button (click)="copyToClipboard(statusEndpoint, 'endpoint')" [title]="endpointJustCopied ? 'Copied!' : 'Copy endpoint'">
                <mat-icon [class.copied]="endpointJustCopied">{{ endpointJustCopied ? 'check' : 'content_copy' }}</mat-icon>
              </button>
            </div>
            <p class="field-hint">Replace <code>{{ '{' }}ACTION_TOKEN{{ '}' }}</code> with the token returned from the start call.</p>
          </div>

          <div class="config-item">
            <label>Authorization Token</label>
            <div class="input-with-copy">
              <input type="text" [value]="token" readonly class="token-input" [class.placeholder-token]="!isTokenAvailable()">
              <button 
                *ngIf="isTokenAvailable()"
                mat-icon-button 
                (click)="copyToClipboard(token, 'token')" 
                [title]="tokenJustCopied ? 'Copied!' : 'Copy token'">
                <mat-icon [class.copied]="tokenJustCopied">{{ tokenJustCopied ? 'check' : 'content_copy' }}</mat-icon>
              </button>
              <button 
                *ngIf="!isTokenAvailable()"
                mat-stroked-button 
                class="regenerate-btn"
                (click)="regenerateToken()" 
                [disabled]="isRegeneratingToken">
                <mat-icon *ngIf="!isRegeneratingToken">refresh</mat-icon>
                <mat-spinner *ngIf="isRegeneratingToken" diameter="20"></mat-spinner>
                {{ isRegeneratingToken ? 'Regenerating...' : 'Regenerate Token' }}
              </button>
            </div>
            <div *ngIf="tokenRegenerationError" class="error-message">
              <mat-icon svgIcon="error"></mat-icon>
              {{ tokenRegenerationError }}
            </div>
          </div>

          <div class="config-item">
            <label>Request Method</label>
            <div class="method-badge">GET</div>
          </div>

          <div class="config-item">
            <label>Headers</label>
            <div class="headers-info">
              <div class="header-item">
                <span class="header-name">Authorization:</span>
                <span class="header-value">Bearer {{ token }}</span>
              </div>
            </div>
          </div>

          <div class="config-item">
            <label>Code Examples</label>
            <mat-tab-group class="code-samples-tabs">
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="curl"></mat-icon>
                    <span>cURL</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getStatusCurlCommand()" [language]="'bash'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getStatusCurlCommand(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="csharp"></mat-icon>
                    <span>C#</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getStatusCSharpCode()" [language]="'csharp'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getStatusCSharpCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="vbnet"></mat-icon>
                    <span>VB.NET</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getStatusVBNetCode()" [language]="'vbnet'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getStatusVBNetCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="python"></mat-icon>
                    <span>Python</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getStatusPythonCode()" [language]="'python'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getStatusPythonCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon>code</mat-icon>
                    <span>Java</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getStatusJavaCode()" [language]="'java'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getStatusJavaCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
            </mat-tab-group>
          </div>
        </div>

        <!-- Results Call Section -->
        <div *ngIf="selectedApiCall === 'results'" class="api-config-section">
          <div class="config-item">
            <label>Endpoint</label>
            <div class="input-with-copy">
              <input type="text" [value]="resultsEndpoint" readonly class="endpoint-input">
              <button mat-icon-button (click)="copyToClipboard(resultsEndpoint, 'endpoint')" [title]="endpointJustCopied ? 'Copied!' : 'Copy endpoint'">
                <mat-icon [class.copied]="endpointJustCopied">{{ endpointJustCopied ? 'check' : 'content_copy' }}</mat-icon>
              </button>
            </div>
            <p class="field-hint">Replace <code>{{ '{' }}ACTION_TOKEN{{ '}' }}</code> with the token returned from the start call.</p>
          </div>

          <div class="config-item">
            <label>Authorization Token</label>
            <div class="input-with-copy">
              <input type="text" [value]="token" readonly class="token-input" [class.placeholder-token]="!isTokenAvailable()">
              <button 
                *ngIf="isTokenAvailable()"
                mat-icon-button 
                (click)="copyToClipboard(token, 'token')" 
                [title]="tokenJustCopied ? 'Copied!' : 'Copy token'">
                <mat-icon [class.copied]="tokenJustCopied">{{ tokenJustCopied ? 'check' : 'content_copy' }}</mat-icon>
              </button>
              <button 
                *ngIf="!isTokenAvailable()"
                mat-stroked-button 
                class="regenerate-btn"
                (click)="regenerateToken()" 
                [disabled]="isRegeneratingToken">
                <mat-icon *ngIf="!isRegeneratingToken">refresh</mat-icon>
                <mat-spinner *ngIf="isRegeneratingToken" diameter="20"></mat-spinner>
                {{ isRegeneratingToken ? 'Regenerating...' : 'Regenerate Token' }}
              </button>
            </div>
            <div *ngIf="tokenRegenerationError" class="error-message">
              <mat-icon svgIcon="error"></mat-icon>
              {{ tokenRegenerationError }}
            </div>
          </div>

          <div class="config-item">
            <label>Request Method</label>
            <div class="method-badge">GET</div>
          </div>

          <div class="config-item">
            <label>Headers</label>
            <div class="headers-info">
              <div class="header-item">
                <span class="header-name">Authorization:</span>
                <span class="header-value">Bearer {{ token }}</span>
              </div>
            </div>
          </div>

          <div class="config-item">
            <label>Code Examples</label>
            <mat-tab-group class="code-samples-tabs">
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="curl"></mat-icon>
                    <span>cURL</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getResultsCurlCommand()" [language]="'bash'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getResultsCurlCommand(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="csharp"></mat-icon>
                    <span>C#</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getResultsCSharpCode()" [language]="'csharp'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getResultsCSharpCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="vbnet"></mat-icon>
                    <span>VB.NET</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getResultsVBNetCode()" [language]="'vbnet'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getResultsVBNetCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon svgIcon="python"></mat-icon>
                    <span>Python</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getResultsPythonCode()" [language]="'python'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getResultsPythonCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <div class="tab-label-content">
                    <mat-icon>code</mat-icon>
                    <span>Java</span>
                  </div>
                </ng-template>
                <div class="code-container">
                  <code [highlight]="getResultsJavaCode()" [language]="'java'" (click)="selectCodeText($event)"></code>
                  <button mat-icon-button class="copy-code-button" (click)="copyToClipboard(getResultsJavaCode(), 'code')" [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                    <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>
              </mat-tab>
            </mat-tab-group>
          </div>
        </div>
      </div>

      <!-- File Upload Options -->
      <div *ngIf="isFileSource" class="file-source-section">
          <div class="info-banner info-box">
            <mat-icon svgIcon="info-circle"></mat-icon>
            <span>This project uses a <strong>{{ getDataSourceTypeName() }}</strong> data source.</span>

          <div class="option-header icon-text gap-md">
            <mat-icon svgIcon="check_circle"></mat-icon>
            <h3>Run with Existing File</h3>
          </div>
          <p>Start the project workflow using the file that was previously uploaded.</p>
          <button mat-raised-button color="primary" (click)="onStartWithExisting()" [disabled]="loading">
            <mat-icon svgIcon="play_arrow"></mat-icon>
            Start with Existing File
          </button>
        </div>

        <div class="divider-with-text">
          <span>or</span>
        </div>

        <div class="option-card card-white rounded-md with-shadow" [class.selected]="!!selectedFile">
          <div class="option-header icon-text gap-md">
            <mat-icon svgIcon="upload_file"></mat-icon>
            <h3>Upload New File</h3>
          </div>
          <p>Upload a new file to replace the existing one before starting the workflow.</p>
          
          <div class="file-input-container">
            <input type="text" 
                   [value]="selectedFile ? selectedFile.name : 'No file selected'" 
                   readonly 
                   class="file-input"
                   placeholder="No file selected">
            <button mat-raised-button (click)="browseFiles()">
              <mat-icon svgIcon="folder_open"></mat-icon>
              Browse
            </button>
          </div>

          <button mat-raised-button 
                  color="primary" 
                  (click)="onUploadNewFile()" 
                  [disabled]="!selectedFile || loading"
                  class="upload-start-btn">
            <mat-icon svgIcon="cloud_upload"></mat-icon>
            Upload & Start
          </button>
        </div>
      </div>

      <!-- No Data Source Configured -->
      <div *ngIf="!data.dataSource" class="help-section">
        <mat-icon class="warning-icon">warning</mat-icon>
        <h3>No Data Source Configured</h3>
        <p>This project does not have a data source configured. Please configure a data source before starting the project.</p>
      </div>

      <div *ngIf="loading" class="loading-overlay">
        <mat-spinner diameter="50"></mat-spinner>
        <p>Starting project...</p>
      </div>
    </div>
  </mat-dialog-content>
</div>
